import React from 'react';
import { auditLogAPI } from '../services/api';

type AuditLogEntry = {
  timestamp: number;
  type: string;
  message: string;
  shortDescription?: string;
  data?: any;
  performedBy?: string;
  performedById?: number;
};

interface TruppAuditlogModalProps {
  truppId: number;
  truppName: string;
  onClose: () => void;
  auditlog?: AuditLogEntry[]; // Parent's audit log state (primary source)
}

const TruppAuditlogModal: React.FC<TruppAuditlogModalProps> = ({ truppId, truppName, onClose, auditlog: parentAuditlog }) => {
  const [entries, setEntries] = React.useState<any[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  
  React.useEffect(() => {
    const loadEntries = async () => {
      setIsLoading(true);
      try {
        let allLogs: any[] = [];
        
        // 1. Primary source: Parent's auditlog state (always up-to-date)
        if (parentAuditlog && parentAuditlog.length > 0) {
          allLogs = [...parentAuditlog];
          console.log(`📋 TruppAuditlogModal: ${parentAuditlog.length} Einträge aus Parent-State`);
        }
        
        // 2. Supplement with API data if online (may have additional entries)
        if (navigator.onLine) {
          try {
            const opsID = parseInt(window.location.pathname.split('/')[2]);
            if (!isNaN(opsID)) {
              const logs = await auditLogAPI.getAuditLog(opsID);
              
              // Convert API logs to local format
              const apiLogs = logs.map((log: any) => {
                let timestamp: number;
                if (log.created_at) {
                  if (typeof log.created_at === 'string') {
                    timestamp = new Date(log.created_at).getTime();
                  } else if (typeof log.created_at === 'number') {
                    timestamp = log.created_at;
                  } else {
                    timestamp = Date.now();
                  }
                } else {
                  timestamp = Date.now();
                }
                
                let data = undefined;
                if (log.logData) {
                  if (typeof log.logData === 'string') {
                    try {
                      data = JSON.parse(log.logData);
                    } catch (e) {
                      data = { raw: log.logData };
                    }
                  } else {
                    data = log.logData;
                  }
                }
                
                // Sicherstellen, dass truppId im data-Objekt vorhanden ist
                if (log.teamID) {
                  if (!data) data = {};
                  if (!data.truppId) data.truppId = log.teamID;
                }
                
                return {
                  type: log.logType,
                  message: log.message,
                  shortDescription: log.shortDescription,
                  timestamp: timestamp,
                  data: data,
                  truppId: log.teamID,
                  performedBy: data?.performedBy,
                  performedById: data?.performedById,
                };
              });
              
              console.log(`📋 TruppAuditlogModal: ${apiLogs.length} Einträge von API`);
              
              // Merge API logs with parent logs, avoiding duplicates
              for (const apiLog of apiLogs) {
                const isDuplicate = allLogs.some(existing =>
                  Math.abs((existing.timestamp || 0) - (apiLog.timestamp || 0)) < 2000 &&
                  existing.type === apiLog.type &&
                  existing.message === apiLog.message
                );
                if (!isDuplicate) {
                  allLogs.push(apiLog);
                }
              }
            }
          } catch (error) {
            console.warn('Failed to load audit logs from API:', error);
          }
        }
        
        // 3. Fallback: localStorage (for offline entries not yet in parent state)
        const localAuditLogs = JSON.parse(localStorage.getItem('auditlog') || '[]');
        if (localAuditLogs.length > 0) {
          for (const localLog of localAuditLogs) {
            const isDuplicate = allLogs.some(existing =>
              Math.abs((existing.timestamp || 0) - (localLog.timestamp || 0)) < 2000 &&
              existing.type === localLog.type &&
              existing.message === localLog.message
            );
            if (!isDuplicate) {
              allLogs.push(localLog);
            }
          }
        }
        
        // Filter logs for this specific team
        const teamLogs = allLogs.filter((log: any) => {
          // Check if the log belongs to this team
          if (log.truppId === truppId) return true;
          if (log.data?.truppId === truppId) return true;
          if (log.data?.teamId === truppId) return true;
          return false;
        });
        
        // Sort by timestamp (oldest first)
        teamLogs.sort((a: any, b: any) => (a.timestamp || 0) - (b.timestamp || 0));
        
        console.log(`📋 TruppAuditlogModal: ${teamLogs.length} Einträge für Trupp ${truppId} (${truppName})`);
        
        setEntries(teamLogs);
      } catch (error) {
        console.error('Error loading audit logs:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadEntries();
  }, [truppId, parentAuditlog, truppName]);
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 12,
        padding: 24,
        maxWidth: 600,
        maxHeight: '80vh',
        overflow: 'auto',
        boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
      }}>
        <h3 style={{ margin: '0 0 16px 0', color: '#c00', textAlign: 'center' }}>Auditlog: {truppName}</h3>
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: 20 }}>Lade Auditlog...</div>
        ) : entries.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 20, color: '#666' }}>Keine Auditlog-Einträge gefunden</div>
        ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {entries.map((entry: any, i: number) => {
            // Format function for countdown display
            const format = (seconds: number) => {
              const absSec = Math.abs(seconds);
              const sign = seconds < 0 ? '-' : '';
              const m = Math.floor(absSec / 60).toString().padStart(2, '0');
              const s = (absSec % 60).toString().padStart(2, '0');
              return `${sign}${m}:${s}`;
            };
                
                // Hilfsfunktion für Felder - handle both API and localStorage data structures
                const aufgabe = entry.data?.aufgabe || entry.data?.task || '-';
                const standort = entry.data?.standort || entry.data?.location || '-';
                const druck = typeof entry.data?.bar === 'number' ? `${entry.data.bar} Bar` : 
                             typeof entry.data?.startPressure === 'number' ? `${entry.data.startPressure} Bar` : '-';
                const funkkanal = entry.data?.funkkanal || entry.data?.radioChannel || '-';
                
                // Verbesserte Datumsanzeige mit robuster Timestamp-Behandlung
                const formatDateTime = (timestamp: number) => {
                  let date: Date;
                  
                  // Handle different timestamp formats
                  if (typeof timestamp === 'string') {
                    // If it's a string, try to parse it
                    date = new Date(timestamp);
                  } else if (typeof timestamp === 'number') {
                    // If it's a number, check if it's milliseconds or seconds
                    if (timestamp < 10000000000) {
                      // Likely seconds, convert to milliseconds
                      date = new Date(timestamp * 1000);
                    } else {
                      // Likely milliseconds
                      date = new Date(timestamp);
                    }
                  } else {
                    // Fallback to current date
                    console.warn('Invalid timestamp format:', timestamp);
                    date = new Date();
                  }
                  
                  // Check if the date is valid
                  if (isNaN(date.getTime())) {
                    console.error('Invalid date created from timestamp:', timestamp);
                    return 'Ungültiges Datum';
                  }
                  
                  // Always show full date and time
                  return date.toLocaleDateString('de-DE', { 
                    day: '2-digit', 
                    month: '2-digit', 
                    year: 'numeric'
                  }) + ' ' + date.toLocaleTimeString('de-DE', { 
                    hour: '2-digit', 
                    minute: '2-digit',
                    second: '2-digit'
                  });
                };
                
            let msg = null;
            if (entry.type === 'Erfasst' || entry.type === 'Trupp erstellt') {
              msg = (<><b>Erfasst:</b> Trupp wurde erfasst</>);
                } else if (entry.type === 'Einsatzstart' || entry.shortDescription === 'In Einsatz geschickt' || entry.type === 'Trupp gestartet') {
              msg = (<><b>Einsatzstart:</b> {aufgabe}, {standort}, {druck}, {funkkanal}</>);
            } else if (entry.type === 'Status geändert') {
              let abfrage = '';
              if (typeof entry.data?.abfrageCountdown === 'number') abfrage = ` (Abfrage ${format(entry.data.abfrageCountdown)})`;
              msg = (<><b>Status aktualisiert:</b> {aufgabe}, {standort}, {druck}, {funkkanal}{abfrage}</>);
            } else if (entry.type === 'Bearbeiten') {
              msg = (<><b>Status bearbeitet:</b> {aufgabe}, {standort}, {druck}, {funkkanal}</>);
            } else if (entry.type === 'Mayday') {
              // Unterscheide zwischen Mayday ausgelöst und Mayday beendet basierend auf der Nachricht
              if (entry.message && entry.message.includes('beendet')) {
                msg = (<><b>Mayday:</b> Mayday wurde beendet</>);
              } else {
                msg = (<><b>Mayday:</b> Mayday wurde ausgelöst</>);
              }
            } else if (entry.type === 'Trupp aus Einsatz' || entry.shortDescription === 'Trupp aus Einsatz') {
              msg = (<><b>Aus Einsatz:</b> Trupp wurde aus Einsatz genommen</>);
            } else if (entry.type === 'Trupp reaktiviert') {
              msg = (<><b>Reaktiviert:</b> Trupp wurde reaktiviert</>);
                } else if (entry.type === 'Druckanpassung') {
                  msg = (<><b>Druckanpassung:</b> Finaler Druck angepasst: {entry.data?.finalPressure} Bar</>);
                } else if (entry.type === 'Mitglieder geändert') {
                  const added = entry.data?.added || [];
                  const removed = entry.data?.removed || [];
                  const addedText = added.length > 0 ? `Hinzugefügt: ${added.map((m: any) => m.vorname + ' ' + m.nachname).join(', ')}` : '';
                  const removedText = removed.length > 0 ? `Entfernt: ${removed.map((m: any) => m.vorname + ' ' + m.nachname).join(', ')}` : '';
                  msg = (<><b>Mitglieder geändert:</b> {addedText} {removedText}</>);
                } else if (entry.type === 'Truppführer geändert') {
                  msg = (<><b>Truppführer geändert:</b> Neuer TF: {entry.data?.neuerTF?.vorname} {entry.data?.neuerTF?.nachname}</>);
                } else if (entry.type === 'Rückmeldung') {
                  msg = (<><b>Rückmeldung:</b> {entry.message}</>);
                } else if (entry.type === 'Alarm stummgeschaltet') {
                  msg = (<><b>Alarm stummgeschaltet</b>: Alarm wurde manuell stummgeschaltet.</>);
                } else if (entry.type === 'Alarm aktiviert') {
                  msg = entry.data?.auto
                    ? (<><b>Alarm aktiviert</b>: Stummschaltung automatisch nach 2 Min. aufgehoben.</>)
                    : (<><b>Alarm aktiviert</b>: Alarm wurde manuell aktiviert.</>);
                } else if (entry.type === 'Benutzerwechsel') {
                  msg = (<><b>Benutzerwechsel</b>: Benutzer gewechselt zu <i>{entry.data?.newUser || 'Unbekannt'}</i>{entry.data?.manual ? ' (manuell)' : ''}</>);
            } else {
              msg = (<>{entry.type}: {entry.message}</>);
            }

            // Benutzer-Info anzeigen (performedBy)
            const performer = entry.performedBy || entry.data?.performedBy;
                
            return (
              <li key={i} style={{ marginBottom: 8, borderBottom: '1px solid #eee', paddingBottom: 4 }}>
                    <span style={{ color: '#888', fontSize: 12, marginRight: 8 }}>{formatDateTime(entry.timestamp)}</span>
                {msg}
                {performer && (
                  <span style={{ color: '#999', fontSize: 11, marginLeft: 6, fontStyle: 'italic' }}>
                    – {performer}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
        )}
        <div style={{ textAlign: 'center', width: '100%' }}>
          <button onClick={onClose} style={{ marginTop: 16, background: '#eee', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 700 }}>Schliessen</button>
        </div>
      </div>
    </div>
  );
};

export default TruppAuditlogModal;
